import {
  useState, useRef, useCallback, useEffect,
} from "react";
import { subscribe, unsubscribe } from "@/shared/services/pusher.service";
import { jobDataManager } from "../services/jobDataManager";
import { isComplete } from "../utils/job-status.utils";
import { jobNotificationSystem } from "./useJobNotifications";
import type { Job, PushedJob } from "../types/jobs.types";

type UseJobUpdates = {
  pushedJobs: PushedJobs
  lastPushedJob: PushedJob | null
  onJobComplete: (job: Job, callback: () => void) => void
  addJobToPushedJobs: (job: PushedJob) => void
  seedInProgressJobs: (jobs: PushedJob[]) => void
  resetJobs: () => void
};

type PushedJobs = Map<string, PushedJob>;

const JOB_UPDATED_EVENT_NAME = "job_updated";

type JobCompletionCallback = () => void;

const addJobToPending = (pendingUpdates: PushedJobs, job: PushedJob): void => {
  pendingUpdates.set(job.uuid, job);
};

const getLastJobFromMap = (jobs: PushedJobs): PushedJob | null => {
  const values = Array.from(jobs.values());
  return values.length > 0 ? values[values.length - 1] : null;
};

const updatePushedJobsState = (prevJobs: PushedJobs, pendingUpdates: PushedJobs): PushedJobs =>
  new Map([...prevJobs, ...pendingUpdates]);

const runJobCompletionCallback = (job: PushedJob, callbacks: Map<string, JobCompletionCallback>): void => {
  const callback = callbacks.get(job.uuid);
  if (callback) {
    callback();
    callbacks.delete(job.uuid);
  }
};

export const useJobUpdates = (channelName: string, onJobCompleted: (job: PushedJob) => void): UseJobUpdates => {
  const [pushedJobs, setPushedJobs] = useState<PushedJobs>(new Map());
  const [lastPushedJob, setLastPushedJob] = useState<PushedJob | null>(null);

  const pendingUpdatesRef = useRef<PushedJobs>(new Map());
  const updateTimeoutRef = useRef<number | null>(null);
  const jobCompletionCallbacksRef = useRef<Map<string, JobCompletionCallback>>(new Map());

  const processPendingUpdates = useCallback((): void => {
    const pendingUpdates = pendingUpdatesRef.current;
    if (pendingUpdates.size === 0) return;

    // Snapshot then clear the ref synchronously. The setPushedJobs updater runs
    // during a later render phase; if it read pendingUpdatesRef directly it could
    // observe an already-cleared Map (the clear below) and drop the update.
    const pendingSnapshot = new Map(pendingUpdates);
    const lastJob = getLastJobFromMap(pendingSnapshot);
    pendingUpdates.clear();
    updateTimeoutRef.current = null;

    setPushedJobs((prevJobs) => {
      const newJobs = updatePushedJobsState(prevJobs, pendingSnapshot);
      jobDataManager.updateJobs(newJobs, lastJob);
      return newJobs;
    });

    setLastPushedJob(lastJob);

    pendingSnapshot.forEach((_, jobUuid) => {
      jobNotificationSystem.notify(jobUuid);
    });
  }, []);

  const handleJobUpdate = useCallback((pushedJob: PushedJob): void => {
    addJobToPending(pendingUpdatesRef.current, pushedJob);

    if (isComplete(pushedJob)) {
      onJobCompleted(pushedJob);
      runJobCompletionCallback(pushedJob, jobCompletionCallbacksRef.current);
    }

    // Process updates immediately instead of batching for debugging
    processPendingUpdates();
  }, [onJobCompleted, processPendingUpdates]);

  // Function to manually add a job to pushedJobs (for newly created jobs)
  const addJobToPushedJobs = useCallback((job: PushedJob): void => {
    setPushedJobs((prevJobs) => {
      const newJobs = new Map(prevJobs);
      newJobs.set(job.uuid, job);

      // Update jobDataManager with the new jobs immediately
      jobDataManager.updateJobs(newJobs, job);

      return newJobs;
    });
    setLastPushedJob(job);

    // Notify components about the new job
    jobNotificationSystem.notify(job.uuid);
  }, []);

  const seedInProgressJobs = useCallback((jobs: PushedJob[]): void => {
    if (jobs.length === 0) return;
    // Read the singleton's current lastPushedJob once, outside the updater: the
    // updater may run more than once (StrictMode/concurrent) and must stay pure.
    const currentLastPushedJob = jobDataManager.getLastPushedJob();
    setPushedJobs((prev) => {
      const seeded = new Map(prev);
      jobs.forEach((job) => {
        if (!seeded.has(job.uuid)) seeded.set(job.uuid, job);
      });
      jobDataManager.updateJobs(seeded, currentLastPushedJob);
      return seeded;
    });
  }, []);

  const resetJobs = useCallback((): void => {
    setPushedJobs(new Map());
    setLastPushedJob(null);
    jobDataManager.updateJobs(new Map(), null);
  }, []);

  const onJobComplete = useCallback((job: Job, callback: JobCompletionCallback): void => {
    if (isComplete(job)) {
      callback();
      return;
    }
    jobCompletionCallbacksRef.current.set(job.uuid, callback);
  }, []);

  const onUnMount = useCallback((): void => {
    const callbacks = jobCompletionCallbacksRef.current;
    callbacks.clear();

    unsubscribe(channelName, JOB_UPDATED_EVENT_NAME, handleJobUpdate);
    if (updateTimeoutRef.current) {
      window.clearTimeout(updateTimeoutRef.current);
    }
  }, [channelName, handleJobUpdate]);

  useEffect(() => {
    subscribe(channelName, JOB_UPDATED_EVENT_NAME, handleJobUpdate);
    return () => onUnMount();
  }, [channelName, handleJobUpdate, onUnMount]);

  return {
    pushedJobs,
    lastPushedJob,
    onJobComplete,
    addJobToPushedJobs,
    seedInProgressJobs,
    resetJobs,
  };
};
